import React, {useMemo} from 'react';
import {useSelectorValue} from "../../common/providers/selectorProvider";
import {useMaxPeriodDateMoment, useOrgEnvFilterFromSourceSelector} from "./values";
import moment from "moment";
import {useTableQuery} from "../../../../common/API/hooks";
import {cleanParams, hasColumn, useIsMetricVisible, useNestedData} from "./utils";
import {
    COLUMNS_TO_IGNORE_ON_MATCH,
    CY_LOADING_OUTPUT,
    LY_LOADING_OUTPUT, PERIOD_FORMAT,
    SALES_SOURCE_AGG_METRICS,
    TABLE_AGG_MLY_STR_DEPT_SALES_RISA
} from "./constants";
import {NOT_AVAILABLE, SELECTOR_ZONE_STORE_TOGGLE} from "../constant";
import {convertNumberToLocaleString} from "../../../../common/utils";
import {useDashboardMetaContext} from "../../common/providers/DashboardMetaProvider";
import {HorizontalTooltipDivider} from '../../../../common/components/HorizontalTooltipDivider';
import {FIELD_BU, FIELD_DEPARTMENT} from "../dashboardDefinition";


export const useAggSalesRISAQuery = (params, useQueryOptions) => {
    const tableSource = useDashboardMetaContext().table_source_by_tag?.['sales'] || TABLE_AGG_MLY_STR_DEPT_SALES_RISA;

    return useTableQuery({
        tableName: tableSource,
        params: {
            ...(params || {}),
        },
        useQueryOptions: {
            ...useQueryOptions,
            // enabled: false
        }
    })
}


export const useBaseSalesData = (params, useQueryOptions = {}) => {
    const hasSalesTableView = useDashboardMetaContext('sales_table_view');

    const partitionDateColumn = 'period';
    const salesFiltersFromDefinition = useDashboardMetaContext('sales_filters') || {}

    const hasDateFilters = hasColumn(params, partitionDateColumn);

    const cleanedParams = cleanParams(params);

    const orgEnvFilters = useOrgEnvFilterFromSourceSelector();

    const isEnabled = hasDateFilters && (useQueryOptions?.enabled !== false) && !hasSalesTableView;

    return useAggSalesRISAQuery(
        {
            ...salesFiltersFromDefinition,
            ...cleanedParams,
            ...orgEnvFilters,
            aggregates: SALES_SOURCE_AGG_METRICS,
        },
        {
            ...useQueryOptions,
            enabled: isEnabled
        }
    );
}

export const useMaxFilterDate = (lastYear, ignoreMaxRecordDateMoment = false, columnFormat='YYYY-MM-DD') => {
    const {data: maxPeriodDateMoment} = useMaxPeriodDateMoment();
    const year = useSelectorValue('year');
    const month = useSelectorValue('month');

    return useMemo(() => {
        if (!year || !month || (!maxPeriodDateMoment && !ignoreMaxRecordDateMoment)) {
            return null;
        }

        if (!ignoreMaxRecordDateMoment && maxPeriodDateMoment.year() === year && maxPeriodDateMoment.month() + 1 === month) {
            // if the max record date is the last day of the month, we can use it as is
            if (lastYear) {
                return maxPeriodDateMoment.subtract(1, 'year').format(columnFormat);
            }
            return maxPeriodDateMoment.format(columnFormat);
        } else {
            if (lastYear) {
                return moment(`${year}-${month}-01`, "YYYY-MM-DD").subtract(1, 'year').endOf('month').format(columnFormat);
            }
            return moment(`${year}-${month}-01`, "YYYY-MM-DD").endOf('month').format(columnFormat);
        }
    }, [maxPeriodDateMoment, year, month, lastYear]);
}

export const useMTDSalesData = (lastYear = false, filters = null, useQueryOptions = {}) => {
    const year = useSelectorValue('year');
    const month = useSelectorValue('month');

    const columnFormat = PERIOD_FORMAT;

    // const partitionDateColumn = useDashboardMetaContext('partition_date_column') || 'transaction_date';
    const partitionDateColumn = 'period';

    const filterMaxDate = useMaxFilterDate(lastYear, false, columnFormat);

    const filterMinDate = useMemo(() => {
        if (!year || !month) {
            return null;
        }
        if (lastYear) {
            return moment(`${year}-${month}-01`, "YYYY-MM-DD").subtract(1, 'year').format(columnFormat);
        }
        return moment(`${year}-${month}-01`, "YYYY-MM-DD").startOf('month').format(columnFormat);
    }, [year, month, lastYear]);

    const newFilters = {
        [`${partitionDateColumn}__gte`]: filterMinDate,
        [`${partitionDateColumn}__lte`]: filterMaxDate,
        ...(filters || {})
    }

    return useBaseSalesData(newFilters, useQueryOptions);
}

export const useYTDSalesData = (lastYear = false, filters = null, useQueryOptions = {}) => {
    // assuming that we can retrieve all metrics from the same API call
    const partitionDateColumn = 'period'
    const year = useSelectorValue('year');

    const columnFormat = PERIOD_FORMAT

    const filterMaxDate = useMaxFilterDate(lastYear, false, columnFormat);
    const filterMinDate = useMemo(() => {
        if (!year) {
            return null;
        }
        if (lastYear) {
            return moment(`${year}-01-01`, "YYYY-MM-DD").subtract(1, 'year').format(columnFormat);
        }
        return moment(`${year}-01-01`, "YYYY-MM-DD").format(columnFormat);
    }, [year, lastYear]);

    const newFilters = {
        [`${partitionDateColumn}__gte`]: filterMinDate,
        [`${partitionDateColumn}__lte`]: filterMaxDate,
        ...(filters || {})
    }


    return useBaseSalesData(newFilters, useQueryOptions);
}


const getTransactionKey = (row) => {
    if (row[FIELD_DEPARTMENT] !== null) {
        return 'dept_txn_count_sum';
    } else if (row[FIELD_BU] !== null) {
        return 'bu_txn_count_sum';
    } else {
        return 'store_txn_count_sum';
    }
}


const computeRowDisplay = (row, isLoading=false, tySalesColumn='ty_gross_sales_sum', lySalesColumn='ly_gross_sales_sum') => {
    const commonMatchedKeys = Object.keys(row).filter(key => {
        return !COLUMNS_TO_IGNORE_ON_MATCH.includes(key);
    })

    const commonDisplayData = commonMatchedKeys.reduce((acc, key) => {
        acc[key] = row[key];
        return acc;
    }, {});


    if (isLoading) {
        return {
            ...CY_LOADING_OUTPUT,
            ...LY_LOADING_OUTPUT
        }
    }

    if (!row) {
        return {};
    }

    // do compute here
    const cySales = parseFloat(row[tySalesColumn]);
    const lySales = parseFloat(row[lySalesColumn]);
    const planSales = row.ty_plan_amount_sum;

    const vsLYSales = lySales ? (cySales / lySales) * 100 : 0;
    const vsPlanSales = planSales ? (cySales / planSales) * 100 : 0;

    // const cyTransactions = parseFloat(row.ty_transaction_count_sum);
    // const lyTransactions = parseFloat(row.ly_transaction_count_sum);
    // if there is a dept level in the data, use dept_txn_count_sum
    // if there is no dept level, but has bu level, use bu_txn_count_sum
    // if there is no dept and bu level, use store_txn_count_sum
    const transactionKey = getTransactionKey(row)

    const cyTransactions = parseFloat(row['ly_' + transactionKey]);
    const lyTransactions = parseFloat(row['ty_' + transactionKey]);

    const planTransactions = 0;  // FIXME

    const vsLYTransactions = lyTransactions ? (cyTransactions / lyTransactions) * 100 : 0;
    const vsPlanTransactions = planTransactions ? (cyTransactions / planTransactions) * 100 : 0;

    const cyATV = cySales / cyTransactions;
    const lyATV = (lySales && lyTransactions) ? lySales / lyTransactions : null;
    const planATV = planSales / planTransactions;

    const vsLYATV = lyATV ? (cyATV / lyATV) * 100 : 0;
    const vsPlanATV = planATV ? (cyATV / planATV) * 100 : 0;

    const cyUnitsSold = row.ty_qty_sum;
    const lyUnitsSold = row.ly_qty_sum;

    const cyUnitsSoldPerTransaction = cyUnitsSold / cyTransactions;
    const lyUnitsSoldPerTransaction = lyUnitsSold / lyTransactions;

    const vsLYUnitsSoldPerTransaction = lyUnitsSoldPerTransaction ? (cyUnitsSoldPerTransaction - lyUnitsSoldPerTransaction) : 0;


    // FIXME: check if we want these columns as standard column names
    const cyDisplayData = {
        cySales,
        cyTransactions,
        cyATV,
        cyUnitsSold,
        cyUnitsSoldPerTransaction
    };
    const lyDisplayData = {
        lySales: lySales || 0,
        vsLYSales,
        lyTransactions: lyTransactions || 0,
        vsLYTransactions,
        lyATV: lyATV || 0,
        vsLYATV,
        lyUnitsSoldPerTransaction: lyUnitsSoldPerTransaction || 0,
        vsLYUnitsSoldPerTransaction,
        lyUnitsSold: lyUnitsSold || 0
    }


    return {
        ...commonDisplayData,
        ...cyDisplayData,
        ...lyDisplayData,
        vsSalesPlan: isLoading ? "Loading..." : vsPlanSales,
        salesPlan: isLoading ? "Loading..." : planSales,
        vsTransactionPlan: isLoading ? "Loading..." : vsPlanTransactions,
        transactionPlan: isLoading ? "Loading..." : planTransactions,
        vsATVPlan: isLoading ? "Loading..." : vsPlanATV,
        atvPlan: isLoading ? "Loading..." : planATV,
    }
}

const computeRowsDisplay = (result, tySalesColumn='ty_gross_sales_sum', lySalesColumn='ly_gross_sales_sum') => {
    // find pairs of rows to compare
    if (!result.data) {
        return null;
    }

    return result.data.map((cyRow) => {
        return computeRowDisplay(
            cyRow,
            false,
            tySalesColumn,
            lySalesColumn
        );
    })
}


export const useMTDCompleteDisplayData = (params, useQueryOptions = {}) => {
    const mtdCYSalesResult = useMTDSalesData(false, params, useQueryOptions);
    const tySalesColumn = useDashboardMetaContext('ty_gross_sales_column') || 'ty_gross_sales_sum';
    const lySalesColumn = useDashboardMetaContext('ly_gross_sales_column') || 'ly_gross_sales_sum';

    const displayData = React.useMemo(() => {
        if (!mtdCYSalesResult.data) {
            return null;
        }
        if (mtdCYSalesResult.isLoading) {
            return null;
        }

        return computeRowsDisplay(mtdCYSalesResult, tySalesColumn, lySalesColumn);
    }, [mtdCYSalesResult, tySalesColumn, lySalesColumn])



    return {
        data: displayData,
        isLoading: mtdCYSalesResult.isLoading,
        isPartialLoading: mtdCYSalesResult.isLoading
    }
}


export const useYTDCompleteDisplayData = (params, useQueryOptions) => {
    const ytdCYSalesResult = useYTDSalesData(false, params, useQueryOptions);
    const tySalesColumn = useDashboardMetaContext('ty_gross_sales_column') || 'ty_gross_sales_sum';
    const lySalesColumn = useDashboardMetaContext('ly_gross_sales_column') || 'ly_gross_sales_sum';

    const displayData = React.useMemo(() => {
        if (!ytdCYSalesResult.data ) {
            return null;
        }
        if (ytdCYSalesResult.isLoading) {
            return null;
        }

        return computeRowsDisplay(ytdCYSalesResult, tySalesColumn, lySalesColumn);
    }, [ytdCYSalesResult, tySalesColumn, lySalesColumn])

    return {
        data: displayData,
        isLoading: ytdCYSalesResult.isLoading,
        isPartialLoading: ytdCYSalesResult.isLoading
    }
}


export const useTopLine = () => {
    const params = useUnifiedSalesParams();

    const hasBusinessUnit = params.businessUnit !== undefined && params.businessUnit !== null && params.businessUnit !== '' && params.businessUnit.length > 0;
    const hasDepartment = params.department !== undefined && params.department !== null && params.department !== '' && params.department.length > 0;

    const fixedParams = useMemo(() => {

        if (!hasBusinessUnit && !hasDepartment && false) {
            return {
                ...params,
                [FIELD_BU + '__is_null']: true,
                [FIELD_DEPARTMENT + '__is_null']: true,
            }
        }
        return params
    }, [hasBusinessUnit, hasDepartment, params])

    const {data: mtdDisplayData, isLoading: mtdIsLoading} = useMTDCompleteDisplayData(fixedParams);
    const {data: ytdDisplayData, isLoading: ytdIsLoading} = useYTDCompleteDisplayData(fixedParams);

    const isSalesMetricVisible = useIsMetricVisible('sales');
    const isTransactionMetricVisible = useIsMetricVisible('transactions');
    const isATVMetricVisible = useIsMetricVisible('atv');
    const isUnitsSoldMetricVisible = useIsMetricVisible('units_sold_per_transaction');

    const isTransactionPlanVisible = useIsMetricVisible('transaction_plan');
    const isATVPlanVisible = useIsMetricVisible('atv_plan');

    const data = [];
    if (isSalesMetricVisible) {
        data.push({
            pk: 'Sales',
            metric: 'sales',
            mtd_actual: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.cySales,
            mtd_actual_tooltip: mtdIsLoading ? [] : [<></>],
            mtd_vs_plan: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.vsSalesPlan,
            mtd_vs_plan_tooltip: mtdIsLoading ? [] : [
                <><b>Sales Plan:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.salesPlan)}</>,
            ],
            mtd_vs_ly: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.vsLYSales,
            mtd_vs_ly_tooltip: mtdIsLoading ? [] : [
                <><b>LY Sales:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.lySales)}</>,
            ],
            ytd_actual: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.cySales,
            ytd_actual_tooltip: ytdIsLoading ? [] : [<></>],
            ytd_vs_plan: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.vsSalesPlan,
            ytd_vs_plan_tooltip: ytdIsLoading ? [] : [
                <><b>Sales Plan:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.salesPlan)}</>,
            ],
            ytd_vs_ly: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.vsLYSales,
            ytd_vs_ly_tooltip: ytdIsLoading ? [] : [
                <><b>LY Sales:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.lySales)}</>,
            ],

        })
    }

    if (isTransactionMetricVisible) {
        const transactionRow = {
            pk: 'Transactions',
            metric: 'transactions',
            mtd_actual: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.cyTransactions,
            mtd_actual_tooltip: mtdIsLoading ? [] : [<></>],
            mtd_vs_plan: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.vsTransactionPlan,
            mtd_vs_plan_tooltip: mtdIsLoading ? [] : [
                <><b>Transaction Plan:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.transactionPlan)}</>,
            ],
            mtd_vs_ly: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.vsLYTransactions,
            mtd_vs_ly_tooltip: mtdIsLoading ? [] : [
                <><b>LY Transactions:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.lyTransactions)}</>,
            ],
            ytd_actual: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.cyTransactions,
            ytd_actual_tooltip: ytdIsLoading ? [] : [<></>],
            ytd_vs_plan: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.vsTransactionPlan,
            ytd_vs_plan_tooltip: ytdIsLoading ? [] : [
                <><b>Transaction Plan:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.transactionPlan)}</>,
            ],
            ytd_vs_ly: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.vsLYTransactions,
            ytd_vs_ly_tooltip: ytdIsLoading ? [] : [
                <><b>LY Transactions:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.lyTransactions)}</>,
            ],
        }
        if (!isTransactionPlanVisible) {
            transactionRow.mtd_vs_plan = NOT_AVAILABLE;
            transactionRow.mtd_vs_plan_tooltip = [];
            transactionRow.ytd_vs_plan = NOT_AVAILABLE;
            transactionRow.ytd_vs_plan_tooltip = [];
        }
        data.push(transactionRow)
    }

    if (isATVMetricVisible) {
        const atvRow = {
            pk: 'Avg Txn Value',
            metric: 'atv',
            mtd_actual: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.cyATV,
            mtd_actual_tooltip: mtdIsLoading ? [] : [
                <HorizontalTooltipDivider />,
                <><b>Formula:</b> Sales / Transactions</>,
                <><b>Sales:</b> {isSalesMetricVisible ? convertNumberToLocaleString(mtdDisplayData?.[0]?.cySales) : "hidden"}</>,
                <><b>Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(mtdDisplayData?.[0]?.cyTransactions) : "hidden"}</>,
            ],
            mtd_vs_plan: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.vsATVPlan,
            mtd_vs_plan_tooltip: mtdIsLoading ? [] : [
                <><b>Avg Txn Value Plan:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.atvPlan)}</>,
                <HorizontalTooltipDivider />,
                <><b>Formula:</b> Sales Plan / Transaction Plan</>,
                <><b>Sales Plan:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.salesPlan)}</>,
                <><b>Transaction Plan:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.transactionPlan)}</>,
            ],
            mtd_vs_ly: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.vsLYATV,
            mtd_vs_ly_tooltip: mtdIsLoading ? [] : [
                <><b>LY Avg Txn Value: </b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.lyATV)}</>,
                <HorizontalTooltipDivider />,
                <><b>LY Sales:</b> {isSalesMetricVisible ? convertNumberToLocaleString(mtdDisplayData?.[0]?.lySales) : "hidden"}</>,
                <><b>LY Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(mtdDisplayData?.[0]?.lyTransactions) : "hidden"}</>,
            ],
            ytd_actual: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.cyATV,
            ytd_actual_tooltip: ytdIsLoading ? [] : [
                <HorizontalTooltipDivider />,
                <><b>Formula:</b> Sales / Transactions</>,
                <><b>Sales:</b> {isSalesMetricVisible ? convertNumberToLocaleString(ytdDisplayData?.[0]?.cySales) : "hidden"}</>,
                <><b>Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(ytdDisplayData?.[0]?.cyTransactions) : "hidden"}</>,
            ],
            ytd_vs_plan: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.vsATVPlan,
            ytd_vs_plan_tooltip: ytdIsLoading ? [] : [
                <><b>Avg Txn Value Plan:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.atvPlan)}</>,
                <HorizontalTooltipDivider />,
                <><b>Formula:</b> Sales Plan / Transaction Plan</>,
                <><b>Sales Plan:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.salesPlan)}</>,
                <><b>Transaction Plan:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.transactionPlan)}</>,
            ],
            ytd_vs_ly: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.vsLYATV,
            ytd_vs_ly_tooltip: ytdIsLoading ? [] : [
                <><b>LY Avg Txn Value: </b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.lyATV)}</>,
                <HorizontalTooltipDivider />,
                <><b>LY Sales:</b> {isSalesMetricVisible ? convertNumberToLocaleString(ytdDisplayData?.[0]?.lySales) : "hidden"}</>,
                <><b>LY Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(ytdDisplayData?.[0]?.lyTransactions) : "hidden"}</>,
            ],
        }
        if (!isATVPlanVisible) {
            atvRow.mtd_vs_plan = NOT_AVAILABLE;
            atvRow.mtd_vs_plan_tooltip = [];
            atvRow.ytd_vs_plan = NOT_AVAILABLE;
            atvRow.ytd_vs_plan_tooltip = [];
        }
        data.push(atvRow)
    }

    if (isUnitsSoldMetricVisible) {
        data.push({
            pk: 'Avg Units Sold',
            metric: 'units_sold_per_transaction',
            mtd_actual: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.cyUnitsSoldPerTransaction,
            mtd_actual_tooltip: mtdIsLoading ? [] : [
                <HorizontalTooltipDivider />,
                <><b>Formula:</b> Units Sold / Transactions</>,
                <><b>Units Sold:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.cyUnitsSold)}</>,
                <><b>Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(mtdDisplayData?.[0]?.cyTransactions) : "hidden"}</>,
            ],
            mtd_vs_plan: NOT_AVAILABLE,
            mtd_vs_ly: mtdIsLoading ? "Loading..." : mtdDisplayData?.[0]?.vsLYUnitsSoldPerTransaction,
            mtd_vs_ly_tooltip: mtdIsLoading ? [] : [
                <><b>LY Avg Units Sold:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.lyUnitsSoldPerTransaction, 2)}</>,
                <HorizontalTooltipDivider />,
                <><b>LY Units Sold:</b> {convertNumberToLocaleString(mtdDisplayData?.[0]?.lyUnitsSold)}</>,
                <><b>LY Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(mtdDisplayData?.[0]?.lyTransactions) : "hidden"}</>,
            ],
            ytd_actual: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.cyUnitsSoldPerTransaction,
            ytd_actual_tooltip: ytdIsLoading ? [] : [
                <HorizontalTooltipDivider />,
                <><b>Formula:</b> Units Sold / Transactions</>,
                <><b>Units Sold:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.cyUnitsSold)}</>,
                <><b>Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(ytdDisplayData?.[0]?.cyTransactions) : "hidden"}</>,
            ],
            ytd_vs_plan: NOT_AVAILABLE,
            ytd_vs_ly: ytdIsLoading ? "Loading..." : ytdDisplayData?.[0]?.vsLYUnitsSoldPerTransaction,
            ytd_vs_ly_tooltip: ytdIsLoading ? [] : [
                <><b>LY Avg Units Sold:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.lyUnitsSoldPerTransaction, 2)}</>,
                <HorizontalTooltipDivider />,
                <><b>LY Units Sold:</b> {convertNumberToLocaleString(ytdDisplayData?.[0]?.lyUnitsSold)}</>,
                <><b>LY Transactions:</b> {isTransactionMetricVisible ? convertNumberToLocaleString(ytdDisplayData?.[0]?.lyTransactions) : "hidden"}</>,
            ],
        })
    }

    return {
        data,
        isLoading: false,  // since we already know the rows, we can set this to false
        isPartialLoading: mtdIsLoading || ytdIsLoading
    }
}

export const useBusinessUnitToDepartmentPerformance = () => {
    const params = useUnifiedSalesParams();
    const autoFlatten = useDashboardMetaContext('bu_to_department_auto_flatten')

    return useNestedData(
        [FIELD_BU, FIELD_DEPARTMENT],
        0,
        params,
        {},
        useMTDCompleteDisplayData,
        useYTDCompleteDisplayData,
        'sales',
        !!autoFlatten
    );
}

export const useZoneToStorePerformance = () => {
    const zoneStoreToggle = useSelectorValue(SELECTOR_ZONE_STORE_TOGGLE);
    const branchNameColumn = useDashboardMetaContext('branch_name_column');

    const params = useUnifiedSalesParams();

    return useNestedData(
        ['zone', branchNameColumn],
        zoneStoreToggle === 'zone' ? 0 : 2,
        params,
        {},
        useMTDCompleteDisplayData,
        useYTDCompleteDisplayData,
        'sales'
    );
}


export const useUnifiedSalesParams = () => {
    // const selectedBuGroups = useSelectorValue('bu_grp');
    const selectedBUs = useSelectorValue(FIELD_BU);
    const selectedDepts = useSelectorValue(FIELD_DEPARTMENT);
    const store = useSelectorValue('store') || [];

    const branchNameColumn = useDashboardMetaContext('branch_name_column');

    const params = {
        // bu_grp: selectedBuGroups,
        [FIELD_BU]: selectedBUs,
        [FIELD_DEPARTMENT]: selectedDepts
    };
    if (store.length > 0) {
        params[branchNameColumn] = store;
    }

    return params;
}
